import { CopyOnHoverValue } from './CopyOnHoverValue';

export interface EmailValueLabelProps {
  value: string | null | undefined;
  fontSize?: 'xs' | 'table' | 'sm' | 'base' | 'lg';
  className?: string;
  /** Enable CSS truncation with ellipsis and title tooltip */
  truncate?: boolean;
}

const fontSizeClasses = {
  xs: 'text-xs',
  table: 'text-[13px]',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
};

/**
 * Display component for email addresses: a mailto: link with the same
 * hover-pill copy affordance phone numbers have (CopyOnHoverValue).
 *
 * Used anywhere an email property renders read-only — lists, record views,
 * the table's popped selection field.
 */
export const EmailValueLabel: React.FC<EmailValueLabelProps> = ({
  value,
  fontSize = 'sm',
  className = '',
  truncate,
}) => {
  const sizeClass = fontSizeClasses[fontSize];
  const truncateClasses = truncate
    ? 'block overflow-hidden text-ellipsis whitespace-nowrap'
    : '';

  if (!value) {
    return <span className={`${sizeClass} ${className}`}>—</span>;
  }

  return (
    <CopyOnHoverValue
      copyText={value}
      copyLabel='Copy email address'
      className={className}
      truncate={truncate}
    >
      <a
        href={`mailto:${encodeURI(value)}`}
        // A plain left-click must NOT launch the mail client — in a table cell
        // it should select the cell (so the click is allowed to bubble), and
        // the copy button is the explicit action. The href stays for
        // right-click "Copy email", keyboard access, and cmd/middle-click
        // (open in new context) for power users.
        onClick={e => e.preventDefault()}
        className={`${sizeClass} ${truncateClasses}`}
        title={truncate ? value : undefined}
        aria-label={`Email ${value}`}
      >
        {value}
      </a>
    </CopyOnHoverValue>
  );
};
